import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/hooks/useUserProfile";

// Markdown format (Gemini 3 - Jan 2026)
// Step parsing is done client-side via regex in parseGuideSteps.ts
export interface MarkdownGuideRecommendations {
  format: "markdown";
  content: string;
  zap_template?: {
    name: string;
    trigger: {
      app_slug: string;
      event: string;
    };
    actions: Array<{
      app_slug: string;
      event: string;
      position: number;
    }>;
    fields?: {
      trigger?: Record<string, string>;
      [key: string]: Record<string, string> | undefined;
    };
  } | null;
  final_flow_contract?: Record<string, unknown> | null;
}

// Structured JSON format (GPT-5.2-pro - Dec 2025)
export interface GuideRecommendations {
  summary: {
    whatWillBeAutomated: string;
    whatRemainsManual: string;
    humanApprovalPoints: string[];
  };
  solutionType: "workflow_automation" | "ai_agent" | "hybrid";
  platform: {
    primary: string;
    whyThis?: string[];
    // Legacy field for backwards compatibility
    runnerUps?: Array<{ platform: string; tradeoff: string }>;
  };
  buildSteps: Array<{
    stepNumber: number;
    action: string;
    input: string;
    expectedResult: string;
    ifStuck: string;
  }>;
  missingFields: Array<{
    field: string;
    question: string;
  }>;
  confidence: {
    score: number;
    justification: string;
  };
  estimatedTimeSavingsPerWeek: number;
  timeSavingsBasis?: string[];
  // Legacy fields for backwards compatibility
  prerequisites?: {
    accountsRequired: string[];
    permissionsNeeded: string[];
    foldersLabelsFields: string[];
    namingConventions: string[];
    testDataNeeded: string[];
  };
  testPlan?: Array<{
    testName: string;
    testType: "happy_path" | "exception" | "failure";
    inputs: string;
    expectedOutputs: string;
    passFailCheck: string;
  }>;
  monitoring?: {
    dailyWeeklyChecks: string[];
    failureAlerts: string;
    pauseRollbackInstructions: string;
    monthlyReview: string[];
  };
}

// Legacy format (kept for backwards compatibility)
export interface LegacyRecommendation {
  title: string;
  approachType: "WORKFLOW" | "AI_ASSIST" | "AGENT" | "HYBRID";
  tools: string[];
  targetSteps: number[];
  whatItAutomates: string;
  workflowOutline: string[];
  prerequisites: string[];
  riskControls: string[];
  expectedImpact: {
    minMinutesSaved: number;
    maxMinutesSaved: number;
    perOccurrence: boolean;
  };
  confidence: "HIGH" | "MEDIUM" | "LOW";
}

export interface ClarityIssue {
  stepNumber: number;
  issue: string;
  suggestion: string;
}

// Helper to check if recommendations is markdown format (newest)
export function isMarkdownFormat(
  recommendations: unknown,
): recommendations is MarkdownGuideRecommendations {
  return (
    typeof recommendations === "object" &&
    recommendations !== null &&
    "format" in recommendations &&
    (recommendations as MarkdownGuideRecommendations).format === "markdown"
  );
}

// Helper to check if recommendations is the structured JSON format
export function isNewGuideFormat(
  recommendations: unknown,
): recommendations is GuideRecommendations {
  return (
    typeof recommendations === "object" &&
    recommendations !== null &&
    "summary" in recommendations &&
    "buildSteps" in recommendations
  );
}

// Solution type from automation plan for card display
export interface SolutionType {
  type: "WORKFLOW_ZAP" | "WORKFLOW_ZAP_AI" | "AI_AGENT";
  label: string;
  description: string;
  reasoning: string;
}

export interface DfyOrderSummary {
  id: string;
  status: "paid" | "in_progress" | "delivered";
  created_at: string;
  estimated_delivery_days: number;
  paid_at: string | null;
}

const PURCHASED_DFY_ORDER_STATUS_SET = new Set([
  "paid",
  "in_progress",
  "delivered",
]);

function getLatestPurchasedDfyOrder(
  rawOrders: unknown,
): DfyOrderSummary | null {
  if (!Array.isArray(rawOrders)) return null;

  const purchasedOrders = rawOrders
    .filter((order): order is DfyOrderSummary => {
      if (!order || typeof order !== "object") return false;
      const typedOrder = order as Record<string, unknown>;
      return (
        typeof typedOrder.id === "string" &&
        typeof typedOrder.created_at === "string" &&
        typeof typedOrder.estimated_delivery_days === "number" &&
        (typedOrder.paid_at === null ||
          typeof typedOrder.paid_at === "string") &&
        PURCHASED_DFY_ORDER_STATUS_SET.has(String(typedOrder.status || ""))
      );
    })
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  return purchasedOrders[0] ?? null;
}

export interface AutomationGuide {
  id: string;
  organization_id?: string | null;
  updated_at?: string;
  sop_id: string | null;
  process_log_id: string | null;
  recommendations?:
    | MarkdownGuideRecommendations
    | GuideRecommendations
    | LegacyRecommendation[];
  sop_clarity_issues?: ClarityIssue[];
  generated_at: string;
  pdf_exports_used?: number | null;
  estimated_time_savings_per_year?: number;
  regeneration_count?: number;
  solution_type?: SolutionType | null;
  source_language?: string; // Language the guide was generated in ('en' or 'es')
  zap_created_at?: string | null; // Timestamp when Zap creation flow started
  zapier_zap_id?: string | null; // Verified Zap ID after successful detection/linking
  latestPurchasedDfyOrder?: DfyOrderSummary | null;
  sop?: {
    title: string;
    purpose?: string;
  };
  process_log?: {
    name: string;
  };
}

export interface RecommendationStatus {
  id: string;
  automation_guide_id: string;
  recommendation_index: number;
  status: "pending" | "implemented" | "dismissed";
}

interface AutomationGuidesData {
  guides: AutomationGuide[];
  statuses: Record<string, RecommendationStatus>;
}

async function fetchAutomationGuides(): Promise<AutomationGuidesData> {
  const { data, error } = await supabase
    .from("automation_guides")
    .select(
      `
      id,
      organization_id,
      updated_at,
      sop_id,
      process_log_id,
      generated_at,
      pdf_exports_used,
      estimated_time_savings_per_year,
      regeneration_count,
      solution_type,
      source_language,
      zap_created_at,
      zapier_zap_id,
      sop:sops(title),
      process_log:process_logs(name)
    `,
    )
    .order("generated_at", { ascending: false });

  if (error) {
    throw new Error(`Error fetching automation guides: ${error.message}`);
  }

  // Type assertion to handle the joined data
  const typedGuides = (data || []).map((guide) => ({
    ...guide,
    estimated_time_savings_per_year: guide.estimated_time_savings_per_year as
      | number
      | undefined,
    solution_type: guide.solution_type as SolutionType | null | undefined,
    sop: guide.sop as { title: string; purpose?: string } | undefined,
    process_log: guide.process_log as { name: string } | undefined,
  }));

  return {
    guides: typedGuides,
    statuses: {},
  };
}

export async function fetchAutomationGuideDetail(
  guideId: string,
): Promise<AutomationGuide> {
  const { data, error } = await supabase
    .from("automation_guides")
    .select(
      `
      id,
      organization_id,
      updated_at,
      sop_id,
      process_log_id,
      recommendations,
      sop_clarity_issues,
      generated_at,
      pdf_exports_used,
      estimated_time_savings_per_year,
      regeneration_count,
      solution_type,
      source_language,
      zap_created_at,
      zapier_zap_id,
      sop:sops(title, purpose),
      process_log:process_logs(name),
      dfy_orders(id, status, created_at, estimated_delivery_days, paid_at)
    `,
    )
    .eq("id", guideId)
    .single();

  if (error || !data) {
    throw new Error(error?.message || "Failed to fetch automation guide");
  }

  const rawDfyOrders = (data as { dfy_orders?: unknown }).dfy_orders;

  return {
    ...(data as Omit<typeof data, "dfy_orders">),
    recommendations: data.recommendations as unknown as
      | MarkdownGuideRecommendations
      | GuideRecommendations
      | LegacyRecommendation[],
    sop_clarity_issues: data.sop_clarity_issues as unknown as
      | ClarityIssue[]
      | undefined,
    estimated_time_savings_per_year: data.estimated_time_savings_per_year as
      | number
      | undefined,
    solution_type: data.solution_type as SolutionType | null | undefined,
    latestPurchasedDfyOrder: getLatestPurchasedDfyOrder(rawDfyOrders),
    sop: data.sop as { title: string; purpose?: string } | undefined,
    process_log: data.process_log as { name: string } | undefined,
  };
}

interface UseAutomationGuidesOptions {
  enabled?: boolean;
}

export const AUTOMATION_GUIDES_QUERY_KEY = ["automation-guides"] as const;
export const AUTOMATION_GUIDES_STALE_TIME_MS = 5 * 60 * 1000;
export const AUTOMATION_GUIDES_GC_TIME_MS = 10 * 60 * 1000;

export const getAutomationGuideDetailQueryKey = (guideId: string) =>
  [...AUTOMATION_GUIDES_QUERY_KEY, "detail", guideId] as const;

/**
 * Shared hook for fetching automation guides with TanStack Query caching.
 * Data is cached for 5 minutes and shared across all components.
 */
export function useAutomationGuides(options: UseAutomationGuidesOptions = {}) {
  const { user } = useAuth();

  return useQuery({
    queryKey: AUTOMATION_GUIDES_QUERY_KEY,
    queryFn: fetchAutomationGuides,
    enabled: (options.enabled ?? true) && !!user,
    staleTime: AUTOMATION_GUIDES_STALE_TIME_MS,
    gcTime: AUTOMATION_GUIDES_GC_TIME_MS,
  });
}

/**
 * Hook to get the invalidation function for automation guides cache.
 * Call this after generating new guides or updating recommendation statuses.
 */
export function useInvalidateAutomationGuides() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: AUTOMATION_GUIDES_QUERY_KEY });
  };
}

/**
 * Utility to invalidate automation guides cache from outside React components.
 */
export function invalidateAutomationGuidesCache(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: AUTOMATION_GUIDES_QUERY_KEY });
}

interface RegenerateGuideParams {
  guideId: string;
  processLogId: string;
  improvementFeedback: string;
  automationScore: {
    score: number;
    tier: string;
    reasoning: string;
    breakdown: Record<string, unknown>;
  };
}

interface RegenerateGuideResult {
  success: boolean;
  jobId?: string;
  status?: string;
  message?: string;
  error?: string;
}

/**
 * Hook to regenerate an automation guide with user-provided improvement feedback.
 * Deletes the existing guide and creates a new one with the corrections applied.
 */
export function useRegenerateGuide() {
  const queryClient = useQueryClient();
  const { organization } = useUserProfile();

  return useMutation<RegenerateGuideResult, Error, RegenerateGuideParams>({
    mutationFn: async ({
      guideId,
      processLogId,
      improvementFeedback,
      automationScore,
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "generate-direct-automation-guide",
        {
          body: {
            processLogId,
            automationScore,
            improvementFeedback,
            existingGuideId: guideId,
            organizationId: organization?.id,
          },
        },
      );

      if (error) {
        throw new Error(error.message || "Failed to regenerate guide");
      }

      return data as RegenerateGuideResult;
    },
    onSuccess: () => {
      // Invalidate guides cache to refresh the list
      queryClient.invalidateQueries({ queryKey: AUTOMATION_GUIDES_QUERY_KEY });
    },
  });
}

/**
 * Hook to mark that the Zap creation flow was started for a guide.
 * This sets a pending anchor timestamp used by zapier-find-zap.
 */
export function useMarkZapCreationStarted() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { guideId: string }>({
    mutationFn: async ({ guideId }) => {
      const { error } = await supabase
        .from("automation_guides")
        .update({
          zap_created_at: new Date().toISOString(),
          zapier_zap_id: null,
        })
        .eq("id", guideId);

      if (error) {
        throw new Error(
          error.message || "Failed to mark Zap creation as started",
        );
      }
    },
    onSuccess: () => {
      // Invalidate guides cache to reflect the change
      queryClient.invalidateQueries({ queryKey: AUTOMATION_GUIDES_QUERY_KEY });
    },
  });
}

/**
 * Hook to ensure guide embeddings exist for RAG-powered automation support.
 * Checks if embeddings exist, and generates them if not.
 * Called when a guide is viewed to ensure embeddings are ready for support queries.
 */
export function useEnsureGuideEmbeddings() {
  return useMutation<
    { sectionsProcessed: number; alreadyExists: boolean },
    Error,
    { guideId: string }
  >({
    mutationFn: async ({ guideId }) => {
      // First check if embeddings already exist
      const { count, error: countError } = await supabase
        .from("guide_section_embeddings")
        .select("*", { count: "exact", head: true })
        .eq("automation_guide_id", guideId);

      if (countError) {
        console.error("Error checking embeddings:", countError);
        throw new Error("Failed to check embeddings");
      }

      // If embeddings already exist, skip generation
      if (count && count > 0) {
        console.log(`Guide ${guideId} already has ${count} embeddings`);
        return { sectionsProcessed: count, alreadyExists: true };
      }

      // Generate embeddings via edge function
      console.log(`Generating embeddings for guide ${guideId}...`);
      const { data, error } = await supabase.functions.invoke(
        "generate-guide-embeddings",
        {
          body: { automationGuideId: guideId },
        },
      );

      if (error) {
        console.error("Error generating embeddings:", error);
        throw new Error("Failed to generate embeddings");
      }

      console.log(`Generated ${data.sectionsProcessed} embeddings for guide`);
      return {
        sectionsProcessed: data.sectionsProcessed,
        alreadyExists: false,
      };
    },
  });
}
